import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { analyticsApi, ApiRequestError, type DistributionBucketDto } from "../../api/api";
import {
  AnalyticsFilterBar,
  type AnalyticsFiltersState,
} from "../../components/ui/AnalyticsFilterBar";
import { PageHeader } from "../../components/ui/PageHeader";

const SCORE_COLORS = ["#fee2e2", "#ffedd5", "#fef9c3", "#dcfce7", "#bbf7d0"];

export function DistributionPage() {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<AnalyticsFiltersState>({
    year: new Date().getFullYear(),
  });
  const [data, setData] = useState<DistributionBucketDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void analyticsApi
      .distribution(filters)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err) => {
        if (!cancelled) {
          setData([]);
          setError(err instanceof ApiRequestError ? err.message : t("analytics_load_failed"));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [filters, t]);

  return (
    <div className="sm-page">
      <PageHeader title={t("distribution")} />
      <AnalyticsFilterBar filters={filters} onChange={setFilters} />
      {error ? <p className="sm-error">{error}</p> : null}
      {loading ? (
        <p className="sm-muted">{t("loading")}</p>
      ) : (
        <div className="sm-card sm-card-pad">
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="score" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" name={t("score_count")}>
                {data.map((_, i) => (
                  <Cell key={i} fill={SCORE_COLORS[i] || "#2E74B5"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
}
